/**
 * Parsing for the hidden markers the Ask AI backend (SPBFinnV2) appends to a
 * stored response:
 *
 *   \n<!-- SOURCES:[...] -->    RAG citations (JSON array)
 *   \n<!-- CHARTS:[...] -->     chart specs from the create_chart tool
 *   \n<!-- TOOLCALLS:[...] -->  tool calls shown in the "Tool Calls" panel
 *   \n<!-- ERROR:{...} -->      the turn failed: {"type": string, "retryable": boolean}
 *
 * Every surface that renders a stored answer (Ask AI history and live polling,
 * the shared-conversation preview, the WBR chatbox) goes through this module so
 * a marker is never rendered and a failed turn never looks like an answer.
 *
 * KEEP-IN-SYNC: components/__tests__/chatboxToolResultsIncomplete.*.test.ts
 * mirror SOURCES_MARKER_RE and TOOLCALLS_MARKER_RE verbatim. TOOLCALLS is
 * deliberately greedy because a tool result can itself contain " -->", which is
 * why the backend writes CHARTS before TOOLCALLS. CHARTS JSON never contains
 * "-->" (the backend escapes ">"), so its regex is non-greedy.
 */
import { ChartSpec, validChartSpecs } from "../answerChart/types";

export interface StoredResponseError {
  /** Error class or service code, e.g. "ServiceUnavailableException". */
  type: string;
  /** True for transient capacity/availability errors. */
  retryable: boolean;
  /** True when inferred from an older turn that saved the raw exception text. */
  legacy?: boolean;
}

export interface ParsedStoredResponse {
  /** Display text with every marker removed. */
  text: string;
  sources?: any[];
  toolCalls?: any[];
  /** Chart specs to render under the answer (create_chart tool). */
  charts?: ChartSpec[];
  /** Present when the turn failed instead of producing an answer. */
  error?: StoredResponseError;
}

// The copy the backend saves for a failed turn (SPBFinnV2.FAILED_TURN_*). Used
// here for older turns that stored the raw exception text instead.
export const FAILED_TURN_RETRYABLE_MESSAGE =
  "FINN couldn't finish this answer because the AI service was temporarily " +
  "busy or unavailable. Please try again in a minute.";
export const FAILED_TURN_GENERIC_MESSAGE =
  "FINN ran into an unexpected problem while answering this question. Please " +
  "try again, and if it keeps failing, let the FINN team know through the " +
  "feedback link.";

// Mirrors SPBFinnV2.RETRYABLE_BEDROCK_ERROR_CODES. Only needed for older turns;
// new turns carry `retryable` in the marker itself.
const RETRYABLE_ERROR_TYPES = new Set([
  "ServiceUnavailableException",
  "ThrottlingException",
  "InternalServerException",
  "ModelNotReadyException",
]);

// Anchored at the end: the backend appends ERROR last, so an answer that merely
// quotes the marker syntax is never mistaken for a failed turn.
const ERROR_MARKER_RE = /\n<!-- ERROR:(\{[^\n]*\}) -->\s*$/;
const SOURCES_MARKER_RE = /\n<!-- SOURCES:(.*?) -->/s;
const TOOLCALLS_MARKER_RE = /\n<!-- TOOLCALLS:(.*) -->/s;
export const CHARTS_MARKER_RE = /\n<!-- CHARTS:(.*?) -->/s;
// An older failed turn: the whole response is a single botocore ClientError
// line, e.g. "An error occurred (ServiceUnavailableException) when calling the
// Converse operation (reached max retries: 2): Bedrock is unable to process
// your request."
const LEGACY_CLIENT_ERROR_RE =
  /^An error occurred \(([A-Za-z0-9]+)\) when calling the [A-Za-z0-9]+ operation[^\n]*$/;

/**
 * Split a decoded stored response into its text and, for a failed turn, the
 * error. Recognizes the backend ERROR marker and, for turns saved before the
 * marker existed, a response that is nothing but a raw botocore error line.
 */
export function extractErrorMarker(text: string): {
  text: string;
  error?: StoredResponseError;
} {
  const match = text.match(ERROR_MARKER_RE);
  if (match && match.index !== undefined) {
    let error: StoredResponseError = { type: "UnknownError", retryable: false };
    try {
      const payload = JSON.parse(match[1]);
      error = {
        type:
          typeof payload?.type === "string" && payload.type
            ? payload.type
            : "UnknownError",
        retryable: payload?.retryable === true,
      };
    } catch {
      // A malformed payload still marks the turn as failed.
    }
    return { text: text.slice(0, match.index), error };
  }
  const legacy = text.trim().match(LEGACY_CLIENT_ERROR_RE);
  if (legacy) {
    const retryable = RETRYABLE_ERROR_TYPES.has(legacy[1]);
    return {
      text: retryable
        ? FAILED_TURN_RETRYABLE_MESSAGE
        : FAILED_TURN_GENERIC_MESSAGE,
      error: { type: legacy[1], retryable, legacy: true },
    };
  }
  return { text };
}

/** Parse a decoded (not hex) stored response into display text and markers. */
export function parseStoredResponse(decoded: string): ParsedStoredResponse {
  const { text: withoutError, error } = extractErrorMarker(decoded);
  let text = withoutError;
  let sources: any[] | undefined;
  let toolCalls: any[] | undefined;
  let charts: ChartSpec[] | undefined;

  const sourcesMatch = text.match(SOURCES_MARKER_RE);
  if (sourcesMatch) {
    text = text.replace(sourcesMatch[0], "");
    try {
      sources = JSON.parse(sourcesMatch[1]);
    } catch {
      sources = undefined;
    }
  }

  // Before TOOLCALLS: the greedy TOOLCALLS match must not start inside CHARTS.
  const chartsMatch = text.match(CHARTS_MARKER_RE);
  if (chartsMatch) {
    text = text.replace(chartsMatch[0], "");
    try {
      const valid = validChartSpecs(JSON.parse(chartsMatch[1]));
      charts = valid.length > 0 ? valid : undefined;
    } catch {
      charts = undefined;
    }
  }

  const toolCallsMatch = text.match(TOOLCALLS_MARKER_RE);
  if (toolCallsMatch) {
    text = text.replace(toolCallsMatch[0], "");
    try {
      toolCalls = JSON.parse(toolCallsMatch[1]);
    } catch {
      toolCalls = undefined;
    }
  }

  return { text, sources, toolCalls, charts, error };
}
